import immediateApi from '@vse/api/immediateApi';

import helpers from '@src/utils/helpers.js';
import * as mqtt_instance from '@src/Main/mqtt_instance';
import { axisModel } from '@ventionco/mm-model';

/**
 * Contains the current state of all controllers present in the jogger.
 */
function JoggerState () {
  this.mSelectedControllerId = undefined;
  this.mControllerStateList = [];
  this.mParentControllerId = undefined;
  this.mHostControllerId = undefined;
  this.mqttSubscriptions = [];

  /**
   * A list of cancellation functions for the mqtt_instance.getClient calls that we make.
   * If we need to clean up before they complete, we'll want to cancel them.
   * @type {[() => void]}
   */
  this.mqttGetClientCancellations = [];
}

JoggerState.prototype.resetSelectedController = function () {
  if (this.mSelectedControllerId !== undefined) {
    this.getSelectedController().selectAxis(undefined); // clear polling loops
    this.setSelectedControllerId(undefined);
  }
};

/**
 * Set the controller id specifying the host of this jogger.
 * @param {String} pControllerId
 */
JoggerState.prototype.setHostController = function (pControllerId) {
  this.mHostControllerId = pControllerId;
};

JoggerState.prototype.getHostControllerId = function () { return this.mHostControllerId; };

/**
 * Set the parent controller id.
 * @param {String} pControllerId
 */
JoggerState.prototype.setParentController = function (pControllerId) {
  this.mParentControllerId = pControllerId;
};

JoggerState.prototype.getParentControllerId = function () { return this.mParentControllerId; };

/**
 * Retrieve the list of controllers.
 * @returns {ControllerState[]} List of controllers.
 */
JoggerState.prototype.listControllerStates = function () {
  return this.mControllerStateList;
};

/**
 * @returns {ControllerState}
 */
JoggerState.prototype.getSelectedController = function () {
  const lSelectedId = this.mSelectedControllerId;
  return this.listControllerStates().find(function (pController) {
    return pController.getId() === lSelectedId;
  });
};

JoggerState.prototype.getSelectedControllerId = function () { return this.mSelectedControllerId; };
JoggerState.prototype.setSelectedControllerId = function (pSelected) {
  if (pSelected === undefined) {
    this.mSelectedControllerId = undefined;
    return;
  }

  if (!this.listControllerStates().find(function (pController) { return pController.getId() === pSelected; })) {
    console.error('Could not select controller with id=' + pSelected);
    return false;
  }

  this.mSelectedControllerId = pSelected;
  return true;
};

/**
 * Add a controller using the JSON instruction that is saved in the Library.
 *
 * @param {Object} pControllerInstruction Controller JSON instruction
 */
JoggerState.prototype.addController = function (pControllerInstruction) {
  this.mControllerStateList.push(new ControllerState(
    pControllerInstruction.parameters.uuid,
    pControllerInstruction.parameters.friendlyName,
    pControllerInstruction.parameters.ipAddress,
    pControllerInstruction.parameters.hardwareVersion,
    this,
  ));
};

/**
 * Rerieve a controller by identifier.
 *
 * @param {String} pControllerId
 * @returns {ControllerState}
 */
JoggerState.prototype.getController = function (pControllerId) {
  return this.listControllerStates().find(function (pController) {
    return pController.getId() === pControllerId;
  });
};

/**
 * Retrieve the axis that is currently selected.
 * @returns {AxisState} Currently selected axis.
 */
JoggerState.prototype.getSelectedAxis = function () {
  const lSelectedController = this.getSelectedController();
  if (!lSelectedController) {
    return undefined;
  }

  if (lSelectedController.getSelectedAxis()) {
    return lSelectedController.getSelectedAxis();
  }

  return undefined;
};

/**
 * Returns whether or not the current tab that we are looking at should be readonly.
 * @returns {Boolean}
 */
JoggerState.prototype.isReadonly = function () {
  // Note Audrey : the 2 first conditions correspond exactly to what the execution-engine checks,
  // and that main exposes through checkIfHasFullAccess

  // Host is undefined.
  if (!this.mHostControllerId) { return false; }
  // Host is the parent controller, allow jogging on all controllers.
  else if (this.mParentControllerId === this.mHostControllerId) { return false; }
  // Host is not the parent controller, allow jogging only on the host.
  else if (this.mSelectedControllerId === this.mHostControllerId) { return false; }
  // All other cases, readonly.
  return true;
};

JoggerState.prototype._addMqttSubscription = function (pRoute, pRegexString, pFunction) {
  const regex = new RegExp(pRegexString);
  const mqttGetClientCancellation = mqtt_instance.getClient((client) => {
    client.subscribe(pRoute, regex, pFunction);
    this.mqttSubscriptions.push({
      route: pRoute,
      regex,
      func: pFunction,
    });
  });

  this.mqttGetClientCancellations.push(mqttGetClientCancellation);
};

JoggerState.prototype.subscribeToIOState = function (pUICallback) {
  const func = (pTopic, pMessage) => {
    const lControllerId = pTopic.split('/')[2];
    const lController = this.getController(lControllerId);
    if (lController) {
      const lIOState = JSON.parse(pMessage);
      lController.setIOState(lIOState);
      if (this.getSelectedControllerId() === lControllerId) {
        pUICallback(JSON.parse(pMessage));
      }
    }
  };
  this._addMqttSubscription('execution-engine/controller/+/io', 'execution-engine/controller/.*/io', func);
};

JoggerState.prototype.getAxisByUuid = function (pUuid) {
  let lAxisList = [];
  this.mControllerStateList.forEach(pController => {
    lAxisList = [...lAxisList, ...pController.listAxes()];
  });
  return lAxisList.find(function (pAxis) { return pAxis.getId() === pUuid; });
};

JoggerState.prototype.subscribeToBrakeState = function (pUICallback) {
  const func = (pTopic, pMessage) => {
    const lAxisUuid = pTopic.split('/')[2];
    const lAxis = this.getAxisByUuid(lAxisUuid);
    if (lAxis) {
      const lBrakeStatus = JSON.parse(pMessage);
      lAxis.setBrakeStatus(lBrakeStatus);
      if (lAxis === this.getSelectedAxis()) {
        pUICallback(lBrakeStatus);
      }
    }
  };
  this._addMqttSubscription('execution-engine/axis/+/brakes', 'execution-engine/axis/.*/brakes', func);
};

JoggerState.prototype.cleanup = function () {
  this.mqttGetClientCancellations.forEach(mqttGetClientCancellation => mqttGetClientCancellation());
  this.mqttGetClientCancellations = [];

  if (this.mqttSubscriptions.length === 0) {
    return Promise.resolve();
  }

  return new Promise((resolve) => {
    // Stop listening to brakes
    mqtt_instance.getClient((client) => {
      this.mqttSubscriptions.forEach(subscription => {
        client.unsubscribe(subscription.route,
          subscription.regex,
          subscription.func);
      });
      this.mqttSubscriptions = [];

      const selectedController = this.getSelectedController();
      if (selectedController) {
        selectedController.stopPollingEndStop();
        selectedController.stopPollingPosition();
      }

      resolve();
    });
  });
};

/**
 * Contains the current state of a single controller.
 * @param {String} pControllerId UUID of the controller.
 */
function ControllerState (pControllerId, pName, pIpAddress, pHardwareVersion, pJoggerState) {
  this.mId = pControllerId;
  this.mName = pName;
  this.mIpAddress = pIpAddress;
  this.mHardWareVersion = pHardwareVersion;
  this.mSelectedAxis = undefined;
  this.mAxisStates = [];
  this.mInputStates = [];
  this.mOutputStates = [];
  this.mEndPollStopHandle = undefined;
  this.mPositionPollHandle = undefined;
  this.mIsControllerAvailable = false;
  this.mJoggerState = pJoggerState;
  this.mIsSmartDriveResponsive = false;
  this.mIOState = undefined;
}

ControllerState.prototype.getId = function () { return this.mId; };
ControllerState.prototype.getName = function () { return this.mName; };
ControllerState.prototype.getIOState = function () { return this.mIOState; };
ControllerState.prototype.setIOState = function (pIOState) { this.mIOState = pIOState; };
ControllerState.prototype.getFormattedName = function () {
  if (this.mJoggerState.listControllerStates().length === 1) { return this.mName; } // when there's only one controller, do not add a parent prefix
  return `${this.mJoggerState.getParentControllerId() === this.mId ? 'Parent:' : 'Child:'} ${this.mName}`;
};
ControllerState.prototype.getIp = function () { return this.mIpAddress; };
ControllerState.prototype.isAvailable = function () { return this.mIsControllerAvailable; };

ControllerState.prototype.fetchSmartDriveStatus = function (pCallback) {
  if (this.mIsSmartDriveResponsive) { pCallback(); return; }

  immediateApi.getSmartDriveStatus(this.getId())
    .then(() => { this.mIsSmartDriveResponsive = true; pCallback(); })
    .catch(e => { console.error('immediateApi.getSmartDriveStatus : smart drive error', e); });
};

/**
 * Gets the axis selected by this Controller.
 * @returns {AxisState}
 */
ControllerState.prototype.getSelectedAxis = function () {
  return this.mSelectedAxis;
};

ControllerState.prototype.selectAxis = function (pId) {
  // Clear polling loops
  this.stopPollingEndStop();
  this.stopPollingPosition();

  if (!pId) {
    this.mSelectedAxis = undefined;
    return;
  }

  this.mSelectedAxis = this.getAxisByUuid(pId);
};

ControllerState.prototype.getAxisByNumber = function (pNumber) {
  return this.listAxes().find(function (pAxis) { return pAxis.getNumber() === pNumber; });
};

ControllerState.prototype.getAxisByUuid = function (pUuid) {
  return this.listAxes().find(function (pAxis) { return pAxis.getId() === pUuid; });
};

/**
 * Begins polling the end stop at a rate of 250ms.
 * @param {Function<Object> => void} pOnEndStopDataReceived
 */
ControllerState.prototype.startPollingEndStop = function (pOnEndStopDataReceived) {
  const lThis = this;
  let lIsPolling = false;
  this.stopPollingEndStop();
  this.mEndPollStopHandle = setInterval(function () {
    if (lIsPolling) { return; }
    lIsPolling = true;

    immediateApi.getEndstopState(lThis.getId()).then(function (data) {
      lIsPolling = false;
      pOnEndStopDataReceived(data);
    }).catch(() => {
      console.error('Error getting endstop state');
      lIsPolling = false;
    });
  }, 250);
};

ControllerState.prototype.stopPollingEndStop = function () {
  this.mEndPollStopHandle && clearInterval(this.mEndPollStopHandle);
  this.mEndPollStopHandle = undefined;
};

ControllerState.prototype.startPollingPosition = function (pOnPositionReceived) {
  const lThis = this;
  let lIsPolling = false;
  this.stopPollingPosition();
  this.mPositionPollHandle = setInterval(function () {
    if (lIsPolling) { return; }
    lIsPolling = true;

    immediateApi.getAllPositionsForController(lThis.mId).then(function (pInfo) {
      lIsPolling = false;

      if (lThis.mPositionPollHandle === undefined) {
        console.warn('Ignoring position received after polling has ended.');
        return;
      }

      if (pInfo) {
        pOnPositionReceived(pInfo);
      }
    }).catch(() => {
      console.error('Error getting positions');
      lIsPolling = false;
    });
  }, 1000);
};

ControllerState.prototype.stopPollingPosition = function () {
  this.mPositionPollHandle && clearInterval(this.mPositionPollHandle);
  this.mPositionPollHandle = undefined;
};

ControllerState.prototype.getAxisNumberFromColor = function (pColor) {
  return helpers.ORDERED_AXIS_COLORS.indexOf(pColor) + 1;
};

ControllerState.prototype.addAxis = function (pAxisInstruction) {
  const lThis = this;
  this.mAxisStates.push(new AxisState(
    this,
    pAxisInstruction.parameters.uuid,
    pAxisInstruction.parameters.friendlyName,
    this.getAxisNumberFromColor(pAxisInstruction.parameters.axisColor),
    pAxisInstruction.parameters.brake,
    pAxisInstruction.parameters.axisType,
    pAxisInstruction.parameters.multiplier,
    pAxisInstruction.parameters.rotation,
    pAxisInstruction.parameters.childrenDrives.map(function (pChild) {
      return {
        color: pChild.color,
        driveNumber: lThis.getAxisNumberFromColor(pChild.color), // TODO: Is this right?
        rotation: pChild.rotation,
      };
    }),
  ));
};

ControllerState.prototype.addPneumatic = function (pPneumaticInstruction) {
  // TO BE IMPLEMENTED
};

ControllerState.prototype.addIo = function (pInst) {
  if (pInst.parameters.name == 'digitalInput' || pInst.parameters.name == 'digitalOutput') { pInst.parameters.deviceType = 'digitalIOModule'; }

  if (pInst.parameters.category && pInst.parameters.category == 'output') {
    this.mOutputStates.push(new OutputState(pInst.parameters.parameters.device, pInst.parameters.parameters.pin, pInst.parameters.aliasName, pInst.parameters.deviceType));
  }
  else if (pInst.parameters.category && pInst.parameters.category == 'input') {
    this.mInputStates.push(new InputState(pInst.parameters.parameters.device, pInst.parameters.parameters.pin, pInst.parameters.aliasName, pInst.parameters.deviceType));
  }
  else {
    console.error('Failed to add IO', pInst);
  }
};

/**
 * Retrieve the list of inputs.
 * @returns {InputState[]}
 */
ControllerState.prototype.listInputs = function () { return this.mInputStates; };

/**
 * Retrieve the list of outputs.
 * @returns {OutputState[]}
 */
ControllerState.prototype.listOutputs = function () { return this.mOutputStates; };

/**
 * Retrieve the state of the axes for this controller.
 * @returns {Array<AxisState>} The list of axes.
 */
ControllerState.prototype.listAxes = function () { return this.mAxisStates; };

function AxisState (pController, pId, pName, pAxisNumber, pBrake, pType, pMultiplier, pRotation, pChildren) {
  this.mController = pController;
  this.mId = pId;
  this.mName = pName;
  this.mAxisNumber = pAxisNumber;
  this.mBrakes = pBrake || 'none';
  this.mBrakeStatus = 'Unknown';
  this.mType = pType;
  this.mMultiplier = pMultiplier;
  this.mChildren = pChildren;
  this.mRotation = pRotation;
  this.mEndstopConfig = undefined;
  this.mMaxSpeed = undefined;
  this.mMaxAcceleration = undefined;
}

AxisState.prototype.getId = function () { return this.mId; };
AxisState.prototype.getName = function () { return this.mName; };
AxisState.prototype.getNumber = function () { return this.mAxisNumber; };
AxisState.prototype.setBrakeStatus = function (pBrakeStatus) { this.mBrakeStatus = pBrakeStatus; };
AxisState.prototype.getBrakeStatus = function () { return this.mBrakeStatus; };
AxisState.prototype.getDrivesList = function () { return [this.mAxisNumber, ...this.mChildren.map(function (pChild) { return pChild.driveNumber; })]; };
AxisState.prototype.getChildren = function () { return this.mChildren; };
AxisState.prototype.hasBrakes = function () { return this.mBrakes === 'present'; };
AxisState.prototype.getType = function () { return this.mType; };
AxisState.prototype.getMultiplier = function () { return this.mMultiplier; };

AxisState.prototype.fetchEndstopConfig = function (pOnComplete) {
  if (this.mController.getSelectedAxis() && this.mController.getSelectedAxis().getId() != this.mId) {
    return;
  }

  const hasAllSensors = this.mEndstopConfig != undefined && this.mEndstopConfig.home != undefined && this.mEndstopConfig.end != undefined;
  if (hasAllSensors) {
    pOnComplete(this.mEndstopConfig);
    return;
  }

  const lThis = this;
  const driveNumber = this.getNumber();
  immediateApi.getEndstopConfig(this.mController.getId(), driveNumber).then(function (data) {
    lThis.mEndstopConfig = data;
    pOnComplete(lThis.mEndstopConfig);
  }).catch(function (e) {
    console.error('error fetching endstop config, retrying.');
    setTimeout(() => lThis.fetchEndstopConfig(pOnComplete), 1000);
  });
};

AxisState.prototype.getMaxAllowedSpeed = function () {
  return axisModel.schemaMap.parameters.axisType.maxSpeedMapping[this.mType];
};

AxisState.prototype.setMaxSpeed = function (pMaxSpeed) { this.mMaxSpeed = pMaxSpeed; };
AxisState.prototype.fetchSpeed = function (pOnComplete) {
  if (this.mController.getSelectedAxis() && this.mController.getSelectedAxis().getId() != this.mId) {
    return;
  }

  if (this.mMaxSpeed) {
    pOnComplete(this.mMaxSpeed);
    return;
  }

  const lThis = this;
  immediateApi.getMaxSpeed(lThis.getId()).then(function (speedJson) {
    lThis.mMaxSpeed = speedJson.maxSpeed;
    pOnComplete(lThis.mMaxSpeed);
  }).catch(function () {
    console.error('error fetching axis speed, retrying.');
    setTimeout(() => lThis.fetchSpeed(pOnComplete), 1000);
  });
};

AxisState.prototype.setMaxAcceleration = function (pMaxAcceleration) { this.mMaxAcceleration = pMaxAcceleration; };
AxisState.prototype.fetchAcceleration = function (pOnComplete) {
  if (this.mController.getSelectedAxis() && this.mController.getSelectedAxis().getId() != this.mId) {
    return;
  }

  if (this.mMaxAcceleration) {
    pOnComplete(this.mMaxAcceleration);
    return;
  }

  const lThis = this;
  immediateApi.getAcceleration(lThis.getId()).then(function (accelerationJson) {
    lThis.mMaxAcceleration = accelerationJson.acceleration;
    pOnComplete(lThis.mMaxAcceleration);
  }).catch(function () {
    console.error('error fetching axis acceleration, retrying.');
    lThis.fetchAcceleration(pOnComplete);
  });
};

// These functions below are loaded when you enter the Controller view.
AxisState.prototype.getRotation = function () { return this.mRotation; };
AxisState.prototype.setRotation = function (pRotation) { this.mRotation = pRotation; };
AxisState.prototype.getRotationList = function () {
  return [this.mRotation, ...this.mChildren.map(function (pChild) { return pChild.rotation; })];
};

AxisState.prototype.fetchAxisInitialData = function (pCallback) {
  const lData = {};
  const lThis = this;

  lThis.fetchEndstopConfig(function (endStopConfig) {
    lThis.fetchSpeed(function (speed) {
      lThis.fetchAcceleration(function (acceleration) {
        immediateApi.getAllPositionsForController(lThis.mController.getId()).then(function (positionInfo) {
          lData.endstopConfig = endStopConfig;
          lData.speed = speed;
          lData.acceleration = acceleration;

          const lDriveLetter = helpers.convert(lThis.getNumber()).toLetter().toUpperCase();
          if (positionInfo[lDriveLetter] != undefined) {
            lData.position = Number.parseFloat(positionInfo[lDriveLetter]).toFixed(1);
          }

          pCallback(lData);
        }).catch(e => {
          console.error('Could not get all axes position, retrying.', e);
          setTimeout(() => lThis.fetchAxisInitialData(pCallback), 1000);
        });
      });
    });
  });
};

function InputState (pDevice, pPin, pName, pDeviceType) {
  this.mDevice = pDevice;
  this.mPin = pPin;
  this.mName = pName;
  this.mDeviceType = pDeviceType;
}

InputState.prototype.getDevice = function () { return this.mDevice; };
InputState.prototype.getPin = function () { return this.mPin; };
InputState.prototype.getName = function () { return this.mName; };
InputState.prototype.getDeviceType = function () { return this.mDeviceType; };

function OutputState (pDevice, pPin, pName, pDeviceType) {
  this.mDevice = pDevice;
  this.mPin = pPin;
  this.mName = pName;
  this.mDeviceType = pDeviceType;
}

OutputState.prototype.getDevice = function () { return this.mDevice; };
OutputState.prototype.getPin = function () { return this.mPin; };
OutputState.prototype.getName = function () { return this.mName; };
OutputState.prototype.getDeviceType = function () { return this.mDeviceType; };

export default {
  JoggerState,
  ControllerState,
  AxisState,
  InputState,
  OutputState,
};
